import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import { taskchecklistApi } from '../../../apis/taskchecklist/taskchecklistApi';
import icon05 from '../../../assets/images/ROOM_TaskFile_Re_05.png';
import { TaskChecklist } from '../../../types/types';
import {ApiClientError} from "../../../apis/errors";
import {ValidationErrorMessages} from "../common/ValidationErrorMessages";
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface UpdateTaskChecklistModalProps {
  checklist: TaskChecklist;
  onSubmitted: () => Promise<void>;
}

interface FormValues {
  content: string;
}

export const UpdateTaskChecklistModal = (
  props: UpdateTaskChecklistModalProps
) => {
  const { checklist, onSubmitted } = props;
  const { success } = useSystemMessage();
  const location = useLocation();
  const { goto } = useAppRouter();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      content: checklist.content,
    },
    reValidateMode: 'onSubmit',
  });

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);

  const onSubmit = async (data: FormValues) => {
    try {
      await taskchecklistApi.update(
        checklist.id,
        { taskCheckContent: data.content, endFlg: checklist.endFlg },
        {}
      );
      await onSubmitted();
      await success('チェックリストを更新しました。');
    } catch(e) {
      await logError(
        location.pathname,
        'チェックリストの更新に失敗しました。',
        e
      );

      if (e instanceof ApiClientError) {
        setApiClientError(e);
        return;
      }

      goto('internalServerError');
    }
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => onSubmitted()}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">チェックリスト編集</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => onSubmitted()}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-modal-content">
              <div className="text-danger text-xs mb-4">
                { apiClientError?.message }
                { (Object.keys(errors).length > 0) && '入力エラーがあります' }
              </div>
              <input
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...register('content', { required: true, maxLength: 10000 })}
                type="text"
                placeholder="チェックリストを入力してください"
                className="text-field"
              />
              {errors.content && errors.content.type === 'required' && (
                <span className="my-2 text-red-500 text-xs">
                  チェックリスト内容は必須です
                </span>
              )}
              {errors.content && errors.content.type === 'maxLength' && (
                <span className="my-2 text-red-500 text-xs">
                  チェックリスト内容は10,000文字以下で指定してください
                </span>
              )}
              <ValidationErrorMessages
                {...{
                  fieldErrors: apiClientError?.errors,
                  field: 'taskCheckContent',
                }}
              />
            </div>
            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={() => onSubmitted()}
                >
                  キャンセル
                </button>
                <button
                  className="btn-primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin mr-4"
                      />
                      保 存
                    </>
                  ) : (
                    '保　存'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};
